import { Controller } from "@hotwired/stimulus";
import { type Crop, move, previewStyle, resize, toBox, withSize } from "../crop_editor/geometry";

type Kind = "token" | "thumbnail";

const DEFAULTS: Record<Kind, Crop> = {
	token: { x: 0.5, y: 0.35, size: 0.6, flip: false },
	thumbnail: { x: 0.5, y: 0.25, size: 0.25, flip: false },
};
const KEY_STEP = 0.01;
// The preview's side in pixels when it has not been laid out; the token's shrinks on narrow screens.
const EDGE: Record<Kind, number> = { token: 160, thumbnail: 100 };
const RING = 0.06;

// The previews are CSS approximations of what the server renders.
export default class extends Controller {
	static targets = ["image", "overlay", "handle", "field", "ghost", "card", "preview", "status", "fallback", "filled", "empty", "flip", "size", "note"];
	static values = { pictureUrl: String, ringColor: String };
	declare readonly imageTarget: HTMLImageElement;
	declare readonly overlayTarget: HTMLElement;
	declare readonly handleTarget: HTMLElement;
	declare readonly fieldTargets: HTMLInputElement[];
	declare readonly ghostTargets: HTMLElement[];
	declare readonly cardTargets: HTMLElement[];
	declare readonly previewTargets: HTMLElement[];
	declare readonly statusTargets: HTMLElement[];
	declare readonly fallbackTargets: HTMLElement[];
	declare readonly filledTarget: HTMLElement;
	declare readonly emptyTargets: HTMLElement[];
	declare readonly flipTarget: HTMLElement;
	declare readonly sizeTarget: HTMLInputElement;
	declare readonly noteTargets: HTMLElement[];
	declare pictureUrlValue: string;
	declare readonly ringColorValue: string;
	kinds: Kind[] = [];
	crops: Partial<Record<Kind, Crop | null>> = {};
	kind: Kind = "token";
	grabbed?: { mode: "move" | "resize"; x: number; y: number; crop: Crop };
	observer?: ResizeObserver;
	objectUrl?: string;

	connect() {
		this.kinds = (["token", "thumbnail"] as Kind[]).filter((kind) => this.fieldTargets.some((input) => input.dataset.crop === kind));
		for (const kind of this.kinds) this.crops[kind] = this.read(kind);
		this.kind = this.kinds[0] ?? "token";
		this.observer = new ResizeObserver(() => this.render());
		this.observer.observe(this.imageTarget);
		this.render();
	}

	disconnect() {
		this.observer?.disconnect();
		this.revoke();
	}

	pick(event: CustomEvent<{ file: File }>) {
		if (!this.mine(event)) return;
		this.revoke();
		this.objectUrl = URL.createObjectURL(event.detail.file);
		this.pictureUrlValue = this.objectUrl;
		this.imageTarget.src = this.objectUrl;
		for (const kind of this.kinds) this.crops[kind] = { ...DEFAULTS[kind] };
		for (const note of this.noteTargets) note.hidden = true;
		this.element.classList.remove("hidden");
		this.commit();
	}

	clear(event: Event) {
		if (!this.mine(event)) return;
		this.revoke();
		this.pictureUrlValue = "";
		this.imageTarget.removeAttribute("src");
		for (const kind of this.kinds) this.crops[kind] = null;
		this.element.classList.add("hidden");
		this.commit();
	}

	// Several forms can share a page; another form's picture is not ours.
	mine(event: Event) {
		const form = (event.target as Element | null)?.closest?.("form");
		return !!form && form === this.element.closest("form");
	}

	revoke() {
		if (this.objectUrl) URL.revokeObjectURL(this.objectUrl);
		this.objectUrl = undefined;
	}

	choose(event: { params: { kind?: string }; currentTarget?: Element | null }) {
		const kind = event.params.kind as Kind;
		if (!this.kinds.includes(kind)) return;
		this.kind = kind;
		this.render();
		// The chip that was clicked is hidden by the switch.
		if (event.currentTarget?.closest?.('[data-crop-editor-target="ghost"]')) this.overlayTarget.focus();
	}

	remove() {
		this.crops[this.kind] = null;
		this.commit();
		this.emptyTargets
			.find((element) => element.dataset.crop === this.kind)
			?.querySelector("button")
			?.focus();
	}

	cut() {
		this.crops[this.kind] = { ...DEFAULTS[this.kind] };
		this.commit();
		this.overlayTarget.focus();
	}

	toggleFlip() {
		const crop = this.crops[this.kind];
		if (crop) this.update({ ...crop, flip: !crop.flip });
	}

	setSize() {
		const crop = this.crops[this.kind];
		if (crop) this.update(withSize(crop, Number(this.sizeTarget.value)));
	}

	grab(event: PointerEvent) {
		const crop = this.crops[this.kind];
		if (!crop) return;
		event.preventDefault();
		this.overlayTarget.setPointerCapture(event.pointerId);
		this.overlayTarget.focus();
		this.grabbed = { mode: event.target === this.handleTarget ? "resize" : "move", x: event.clientX, y: event.clientY, crop };
	}

	drag(event: PointerEvent) {
		if (!this.grabbed) return;
		const { width, height } = this.size();
		const dx = event.clientX - this.grabbed.x;
		const dy = event.clientY - this.grabbed.y;
		this.update(this.grabbed.mode === "move" ? move(this.grabbed.crop, dx, dy, width, height) : resize(this.grabbed.crop, (dx + dy) / 2, width, height));
	}

	drop() {
		this.grabbed = undefined;
	}

	key(event: KeyboardEvent) {
		const crop = this.crops[this.kind];
		const arrows: Record<string, [number, number]> = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] };
		const arrow = arrows[event.key];
		if (!crop || !arrow) return;
		event.preventDefault();
		const { width, height } = this.size();
		if (event.shiftKey) this.update(withSize(crop, crop.size + (arrow[0] + -arrow[1]) * KEY_STEP));
		else this.update(move(crop, arrow[0] * KEY_STEP * width, arrow[1] * KEY_STEP * height, width, height));
	}

	update(crop: Crop) {
		this.crops[this.kind] = crop;
		this.commit();
	}

	commit() {
		for (const kind of this.kinds) this.write(kind);
		this.render();
	}

	size() {
		return { width: this.imageTarget.clientWidth, height: this.imageTarget.clientHeight };
	}

	field(kind: Kind, key: string) {
		return this.fieldTargets.find((target) => target.dataset.crop === kind && target.dataset.key === key);
	}

	read(kind: Kind): Crop | null {
		const [x, y, size] = ["x", "y", "size"].map((key) => Number.parseFloat(this.field(kind, key)?.value ?? ""));
		if (![x, y, size].every(Number.isFinite)) return null;
		return { x, y, size, flip: this.field(kind, "flip")?.value === "1" };
	}

	write(kind: Kind) {
		const crop = this.crops[kind];
		const round = (value: number) => String(Math.round(value * 10000) / 10000);
		const values: Record<string, string> = {
			x: crop ? round(crop.x) : "",
			y: crop ? round(crop.y) : "",
			size: crop ? round(crop.size) : "",
			flip: crop ? (crop.flip ? "1" : "0") : "",
		};
		for (const [key, value] of Object.entries(values)) {
			const input = this.field(kind, key);
			if (input) input.value = value;
		}
	}

	render() {
		const crop = this.crops[this.kind] ?? null;
		const { width, height } = this.size();
		const ready = width > 0 && height > 0;

		this.overlayTarget.hidden = !crop || !ready;
		if (crop) {
			this.place(this.overlayTarget, this.kind, crop, width, height);
			this.sizeTarget.value = String(crop.size);
			this.flipTarget.setAttribute("aria-pressed", String(crop.flip));
			this.flipTarget.classList.toggle("odo-button__primary", crop.flip);
			this.flipTarget.classList.toggle("odo-button__alt", !crop.flip);
		}
		this.filledTarget.hidden = !crop;
		for (const element of this.emptyTargets) element.hidden = !!crop || element.dataset.crop !== this.kind;

		for (const ghost of this.ghostTargets) {
			const kind = ghost.dataset.crop as Kind;
			const other = this.crops[kind];
			ghost.hidden = !other || kind === this.kind || !ready;
			if (other && ready) this.place(ghost, kind, other, width, height);
		}

		for (const card of this.cardTargets) {
			const active = card.dataset.crop === this.kind;
			card.setAttribute("aria-pressed", String(active));
			card.classList.toggle("border-link", active);
			card.classList.toggle("ring-1", active);
			card.classList.toggle("ring-inset", active);
			card.classList.toggle("ring-link", active);
			card.classList.toggle("border-line", !active);
		}
		for (const status of this.statusTargets) {
			const kind = status.dataset.crop as Kind;
			const state = kind === this.kind ? "editing" : this.crops[kind] ? "edit" : "cut";
			status.hidden = status.dataset.state !== state;
		}

		for (const element of this.fallbackTargets) element.hidden = !!this.crops[element.dataset.crop as Kind];
		if (ready) for (const element of this.previewTargets) this.preview(element, element.dataset.crop as Kind, height / width);
	}

	place(element: HTMLElement, kind: Kind, crop: Crop, width: number, height: number) {
		const box = toBox(crop, width, height);
		Object.assign(element.style, {
			left: `${box.left}px`,
			top: `${box.top}px`,
			width: `${box.side}px`,
			height: `${box.side}px`,
			borderRadius: kind === "token" ? "50%" : "0",
		});
	}

	preview(element: HTMLElement, kind: Kind, aspect: number) {
		const crop = this.crops[kind];
		if (!crop) {
			Object.assign(element.style, { backgroundImage: "none", boxShadow: "none", transform: "none" });
			return;
		}
		const edge = element.clientWidth || EDGE[kind];
		const ring = kind === "token" && this.ringColorValue ? `inset 0 0 0 ${Math.round(edge * RING)}px ${this.ringColorValue}` : "none";
		Object.assign(element.style, { backgroundImage: `url("${this.pictureUrlValue}")`, boxShadow: ring, ...previewStyle(crop, aspect, edge) });
	}
}
